import React, { useEffect, useRef, useState } from 'react'
import Heading from '../../../../components/Heading'
import Text from '../../../../components/Text'
import { useTranslation } from 'react-i18next'
import { useIndividualizationContext } from '../../../../contexts/individualizationContext'
import { fabric } from 'fabric'
import Icon from '../../../../components/Icon'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { v4 as uuid } from 'uuid'
import Card from '../../../../components/Card'
import RectangleSVG from './SVGs/RectangleSVG'
import CircleSVG from './SVGs/CircleSVG'
import EllipseSVG from './SVGs/EllipseSVG'
import PolygonSVG from './SVGs/PolygonSVG'
import cx from 'classnames'
import styles from './DefineAreas.module.scss'
import ActiveSpotCard from './ActiveSpotCard'
import Sidebar from './Sidebar'
import {
    getCircle,
    getEllipse,
    getPolygon,
    getRectangle,
} from '../../../../helpers/addFabricElement'
import {
    resizeEverything,
    changeBackground,
    addCanvasToWrapper,
} from '../../../../helpers/resizeEverything'

const DefineAreas: React.FC = () => {
    const { t } = useTranslation(['individualization'])
    const {
        canvas,
        activeStep,
        templates,
        spots,
        addSpot,
        removeSpot,
        addEdgeToPolygon,
        removeEdgeFromPolygon,
        helpers,
        initializeHelpers,
        examples,
        setExamples,
        activeSpotId,
        activeSpot,
        changeActiveSpot,
        activeTemplateId,
        activeTemplate,
        fonts,
        setActiveTemplateId,
    } = useIndividualizationContext()
    const wrapper = useRef<HTMLDivElement>(null)
    const [showTemplatesSidebar, setShowTemplatesSidebar] = useState<boolean>(
        templates.length > 1
    )
    const [showAddSectionSidebar, setShowAddSectionSidebar] =
        useState<boolean>(true)
    const resizeObserver = useRef<ResizeObserver | null>(null)
    const timer = useRef<undefined | number | NodeJS.Timeout>(undefined)

    const changeExampleText = (
        spot_id: string | number,
        data: Partial<fabric.Text>
    ) => {
        const spot = spots.find((s) => s.data?.id === spot_id)
        if (!spot) return

        setExamples((prevExamples) => {
            let example = prevExamples.find((e) => e.data.spot_id === spot_id)

            if (!example) {
                example = new fabric.Text('', {
                    originX: 'center',
                    originY: 'center',
                    left: spot.left,
                    top: spot.top,
                    angle: spot.angle,
                    fontSize: spot.data?.font_size ?? 40,
                    fontFamily: fonts.length > 0 ? fonts[0].name : undefined,
                    fill: spot.data.text_color ?? '#000',
                    selectable: true,
                    centeredScaling: true,
                    hasBorders: false,
                    hasControls: false,
                    cornerSize: 12,
                    data: {
                        spot_id: spot_id,
                        template_id: spot.data?.template_id,
                        type: 'Example',
                    },
                })

                canvas.add(example)
            }

            example.set(data)

            return [
                ...prevExamples.filter((e) => e.data.spot_id !== spot_id),
                example,
            ]
        })

        canvas.requestRenderAll()
    }

    const changePage = (template_id: string | number) => {
        const result = templates.find((template) => template.id === template_id)
        if (result && template_id !== activeTemplateId) {
            setActiveTemplateId(template_id)
            changeActiveSpot(null)
        }
    }

    useEffect(() => {
        if (activeStep !== 3 || !wrapper.current) return

        resizeObserver.current = new ResizeObserver(() => {
            clearTimeout(timer.current)
            timer.current = setTimeout(() => {
                if (wrapper.current) {
                    resizeEverything(canvas, wrapper.current)
                    addCanvasToWrapper(canvas, wrapper.current)
                }
            }, 500)
        })

        resizeObserver.current.observe(wrapper.current)

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect()
                resizeObserver.current = null
            }
            if (timer.current) {
                clearTimeout(timer.current)
                timer.current = undefined
            }
        }
    }, [canvas, activeStep])

    useEffect(() => {
        if (activeTemplate) {
            changeBackground(activeTemplate, canvas)
        }
    }, [activeTemplate?.src, activeTemplate, canvas])

    useEffect(() => {
        if (activeStep !== 3 || !activeTemplateId) return

        canvas.remove(
            ...canvas
                .getObjects()
                .filter(
                    (obj) =>
                        obj.data?.template_id !== activeTemplateId ||
                        obj.data?.type === 'Measurement'
                )
        )

        spots.forEach((spot) => {
            if (spot.data.template_id === activeTemplateId) {
                const found = canvas
                    .getObjects()
                    .find((obj) => obj.data?.id === spot.data.id)
                if (!found) canvas.add(spot)
            }

            const spotHelpers = helpers.get(spot.data.id) ?? []
            spotHelpers.forEach((helper) => {
                if (helper.data.template_id === activeTemplateId) {
                    const found = canvas
                        .getObjects()
                        .find((obj) => obj.data?.id === helper.data.id)
                    if (!found) canvas.add(helper)
                }
            })
        })

        canvas.renderAll()
    }, [activeStep, spots, helpers, activeTemplateId, canvas])

    const addCircle = () => {
        if (activeTemplateId) {
            const circle = getCircle(canvas, activeTemplateId)
            addSpot(circle)
            canvas.add(circle)
            canvas.setActiveObject(circle)
        }
    }

    const addEllipse = () => {
        if (activeTemplateId) {
            const ellipse = getEllipse(canvas, activeTemplateId)
            addSpot(ellipse)
            canvas.add(ellipse)
            canvas.setActiveObject(ellipse)
        }
    }

    const addRectangle = () => {
        if (activeTemplateId) {
            const rectangle = getRectangle(canvas, activeTemplateId)
            addSpot(rectangle)
            canvas.add(rectangle)
            canvas.setActiveObject(rectangle)
        }
    }

    const addPolygon = () => {
        const id = uuid()
        if (activeTemplateId) {
            const polygon = getPolygon(canvas, activeTemplateId, id)

            polygon.setControlsVisibility({
                tl: true,
                tr: true,
                br: true,
                bl: true,
                mt: false,
                mb: false,
                ml: false,
                mr: false,
                mtr: false,
            })

            addSpot(polygon)
            canvas.add(polygon)
            canvas.setActiveObject(polygon)
            initializeHelpers(polygon)
        }
    }

    const changeFabricActive = (obj: fabric.Object) => {
        if (obj instanceof fabric.Polygon) {
            const circleList = canvas.getObjects('circle')
            const edge = circleList.find(
                (p) => p.data?.polygon_id === obj.data.id
            )
            if (edge) canvas.setActiveObject(edge)
        } else {
            canvas.setActiveObject(obj)
        }
        canvas.requestRenderAll()
    }

    const handleAddEdgeButton = (id: string | number) => {
        if (activeTemplateId) addEdgeToPolygon(id, activeTemplateId, canvas)
    }

    const handleRemoveEdgeButton = (id: string | number) => {
        removeEdgeFromPolygon(id)
    }

    const handleRemoveSpotButton = (id: string | number) => {
        removeSpot(id, canvas)
    }

    const toggleTemplatesSidebar = () => {
        setShowTemplatesSidebar((prev) => !prev)
        if (!showTemplatesSidebar) setShowAddSectionSidebar(false)
    }

    const toggleAddSectionSidebar = () => {
        setShowAddSectionSidebar((prev) => !prev)
        if (!showAddSectionSidebar) setShowTemplatesSidebar(false)
    }

    useEffect(() => {
        if (activeSpot) setShowTemplatesSidebar(false)
    }, [activeSpot])

    return activeStep === 3 ? (
        <>
            <Heading level={3} classes={'zr-mt-4'}>
                {t('progress.define_areas')}
            </Heading>
            <Text classes={'zr-my-4'}>{t('define_areas.description')}</Text>
            <div className={cx(styles['page-wrapper'])}>
                <div ref={wrapper} className={cx(styles['wrapper-container'])}>
                    <Sidebar
                        templates={templates}
                        changeTemplate={changePage}
                        active={activeTemplateId}
                        show={showTemplatesSidebar}
                        toggleSidebar={toggleTemplatesSidebar}
                        colors={{ background: '#FC0', text: '#000' }}
                    />
                    <div
                        className={cx(styles['button-wrapper'])}
                        style={{
                            top: showAddSectionSidebar ? '1rem' : '-15rem',
                        }}
                    >
                        <div
                            onClick={() => addRectangle()}
                            className={cx(styles['add-icon'])}
                        >
                            <RectangleSVG />
                        </div>
                        <div
                            onClick={() => addCircle()}
                            className={cx(styles['add-icon'])}
                        >
                            <CircleSVG />
                        </div>
                        <div
                            onClick={() => addEllipse()}
                            className={cx(styles['add-icon'])}
                        >
                            <EllipseSVG />
                        </div>
                        <div
                            onClick={() => addPolygon()}
                            className={cx(styles['add-icon'])}
                        >
                            <PolygonSVG />
                        </div>
                        <div>
                            <div
                                className={cx(styles['menu-icon'])}
                                onClick={toggleAddSectionSidebar}
                            >
                                <Icon
                                    icon={
                                        !showAddSectionSidebar
                                            ? faChevronDown
                                            : faChevronUp
                                    }
                                    size={'lg'}
                                    color={'#000'}
                                />
                            </div>
                            <div className={cx(styles['small-text'])}>
                                {t('define_areas.add_area')}
                            </div>
                        </div>
                    </div>
                </div>
                {activeTemplate && (
                    <div className={cx(styles['right-wrapper'])}>
                        <Heading level={3}>{activeTemplate.name}</Heading>
                        {activeSpot && (
                            <Heading level={4}>
                                {t('define_areas.chosen')}
                            </Heading>
                        )}
                        {activeSpot && (
                            <ActiveSpotCard
                                activeSpot={activeSpot}
                                removeSpot={handleRemoveSpotButton}
                                addEdge={handleAddEdgeButton}
                                removeEdge={handleRemoveEdgeButton}
                                changeExampleText={changeExampleText}
                                exampleTexts={examples}
                            />
                        )}
                        {((spots.length === 1 && activeSpot === undefined) ||
                            spots.length >= 2) && (
                            <Heading level={4}>
                                {t('define_areas.other')}
                            </Heading>
                        )}
                        {spots.map((spot, index) => {
                            if (
                                spot.data.id !== activeSpotId &&
                                spot.data.template_id === activeTemplateId
                            )
                                return (
                                    <Card
                                        title={spot.data.name}
                                        key={`other_spot-${index}`}
                                        classes={cx(styles['other-card'])}
                                        onClick={() => changeFabricActive(spot)}
                                    >
                                        <></>
                                    </Card>
                                )
                            else return null
                        })}
                    </div>
                )}
            </div>
        </>
    ) : null
}

export default DefineAreas
